import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Markdown } from "../markdown";
import { lookupRenderer } from "../components/index";
import { groupIntoRuns, type Run, type UserRow } from "./messageRuns";
import { branchPointOf, forkedAt } from "./branchPoints";
import { parseHandover, speakerNames } from "./handover";
import { formatClock, parseWatching } from "../../../shared/watching/line.ts";
import { WatchingChip } from "./videoSeek";
import { cardTarget, openableSession, queuedJob, readDelegateResult, type DelegateStatus } from "./delegateCard";
import { readOneShotResult, runOf, type OneShotRunRef, type OneShotStatus } from "./oneShotCard";
import { RunTrace } from "./RunTrace";
import { noticeFor, textOf } from "./notices";
import { imageSources } from "./contentImages";
import { api } from "../api";
import type { AssistantMessage, Message, ProfileSummary, SessionSummary, ToolCallContent, ToolResultMessage } from "../types";

function Collapsible({ label, tone, children }: { label: React.ReactNode; tone?: string; children: React.ReactNode }) {
	const [open, setOpen] = useState(false);
	return (
		<div className={`collapsible${tone ? ` ${tone}` : ""}`}>
			<button className="collapsible-head" onClick={() => setOpen((v) => !v)}>
				<span className="caret">{open ? "▾" : "▸"}</span>
				{label}
			</button>
			{open && <div className="collapsible-body">{children}</div>}
		</div>
	);
}

/** The one argument worth showing on a collapsed row: whatever the call is mostly about. */
function summarizeArgs(args: unknown): string {
	const a = args as Record<string, unknown> | undefined;
	const first = a && (a.command ?? a.file_path ?? a.path ?? a.url ?? a.pattern ?? a.query);
	return typeof first === "string" ? first.slice(0, 120) : JSON.stringify(args ?? {}).slice(0, 120);
}

/** Opening the session a `delegate` call runs in, and the ones announced while they run. */
export interface DelegateProps {
	/** Child session by call id, from `delegate_started`; the result carries it once the job is over. */
	started: Record<string, string>;
	/** A background job's session by job id, from the snapshot and `delegate_job_started`. */
	jobs?: Record<string, string>;
	onOpen: (id: string) => void;
}

const DELEGATE_STATUS: Record<DelegateStatus | "running", string> = {
	running: "工作中…",
	done: "已完成",
	timeout: "超时",
	stopped: "已停止",
	failed: "失败",
};

/**
 * A `delegate` call as a card: who got the job, what it was, how it ended, and the child session
 * to open, or for a Claude Code job the model, effort and run it reported, with nothing to open.
 * The reply itself stays behind the fold: the caller's next message is what to read.
 */
function DelegateCard({ callId, args, result, delegates }: { callId: string; args: unknown; result?: ToolResultMessage; delegates?: DelegateProps }) {
	const a = (args ?? {}) as { task?: string };
	const read = result ? readDelegateResult(textOf(result.content)) : undefined;
	const status: DelegateStatus | "running" = !result ? "running" : (read?.status ?? (result.isError ? "failed" : "done"));
	const queued = result && !read ? queuedJob(textOf(result.content)) : undefined;
	const sessionId = openableSession(read, delegates?.started[callId], queued ? delegates?.jobs?.[queued] : undefined);
	const worker = read?.codingWorker ?? read?.claudeCode;
	const workerName = read?.codingWorker ? "Codex" : "Claude Code";
	const task = (a.task ?? "").split("\n")[0] ?? "";
	return (
		<Collapsible
			tone={`tool delegate ${status === "running" ? "running" : status === "done" ? "done" : "error"}`}
			label={
				<>
					<span className="tool-name">委派给 {cardTarget(args, read)}</span>
					{worker && <span>{workerName} · {worker.model} · {worker.effort}</span>}
					<span className="tool-summary">{task}</span>
					<span className="tool-state">{DELEGATE_STATUS[status]}</span>
					{sessionId && delegates && (
						<button
							className="link"
							onClick={(e) => {
								e.stopPropagation();
								delegates.onOpen(sessionId);
							}}
						>
							打开
						</button>
					)}
				</>
			}
		>
			<pre className="code-block">
				<code>{a.task ?? ""}</code>
			</pre>
			{worker && (worker.session || worker.turns !== undefined) && (
				<p className="hint">
					{[worker.session && `${workerName} 会话 ${worker.session}`, worker.turns !== undefined && `${worker.turns} 轮`, worker.cost !== undefined && `$${worker.cost.toFixed(2)}`].filter(Boolean).join(" · ")}
				</p>
			)}
			{result && (
				<pre className="code-block">
					<code>{read?.reply ?? textOf(result.content) ?? "（无输出）"}</code>
				</pre>
			)}
		</Collapsible>
	);
}

/** The runs of `one_shot` calls announced while they run, and opening a one-shot on the loops page. */
export interface OneShotProps {
	/** The run by call id, from `one_shot_started`; the result carries it once the call is over. */
	runs: Record<string, OneShotRunRef>;
	/** The check each call's run is on, by call id, from `one_shot_checking`. */
	checks?: Record<string, OneShotCheck>;
	onOpen?: (loopId: string) => void;
}

/** A check the platform started on a one-shot's run, and when the card heard of it. */
export interface OneShotCheck {
	index: number;
	of: number;
	run: string;
	at: number;
}

const ONE_SHOT_STATUS: Record<OneShotStatus | "running" | "checking" | "refused", string> = {
	running: "运行中…",
	checking: "验收中…",
	passed: "已通过",
	failed: "未通过",
	aborted: "已停止",
	interrupted: "已中断",
	refused: "未开始",
};

/**
 * The run of a one-shot still going that this card missed the announcement of, as after a reload:
 * the one its follow-up `id` names, else the newest running one-shot of its `change`.
 */
function useRunningOneShot(look: { id?: string; change?: string } | undefined): OneShotRunRef | undefined {
	const [found, setFound] = useState<OneShotRunRef>();
	const wanted = look !== undefined;
	const id = look?.id;
	const change = look?.change;
	useEffect(() => {
		if (!wanted) return;
		let cancelled = false;
		void (async () => {
			try {
				const loopId = id ?? (await api.loops()).loops.find((l) => l.oneShot && l.change === change && l.status === "running")?.id;
				if (!loopId) return;
				const { loop } = await api.loop(loopId);
				const last = loop.iterations.at(-1);
				if (!cancelled && last) setFound({ loopId, iterationId: last.id });
			} catch {
				// The card still says it is running; the loops page has the rest.
			}
		})();
		return () => void (cancelled = true);
	}, [wanted, id, change]);
	return found;
}

/**
 * A `one_shot` call as a card: the change, which run it is and how it ended, with the run's trace
 * under it, live while the coding worker works, and a way to the one-shot on the loops page. The brief and
 * the result stay behind the fold: dev's next message is what to read.
 */
function OneShotCard({ callId, args, result, live, oneShots }: { callId: string; args: unknown; result?: ToolResultMessage; live: boolean; oneShots?: OneShotProps }) {
	const a = (args ?? {}) as { goal?: string; change?: string; id?: string; follow_up?: string };
	const read = result ? readOneShotResult(textOf(result.content)) : undefined;
	// A call with no result is running only while the conversation is; otherwise the server cut it off.
	const going = result ? (read?.status ?? (result.isError ? "failed" : "refused")) : live ? "running" : "interrupted";
	// The coding worker ended while the call goes on: the platform is running the checks.
	const [ended, setEnded] = useState(false);
	const check = oneShots?.checks?.[callId];
	const status = going === "running" && (check || ended) ? "checking" : going;
	const announced = oneShots?.runs[callId];
	const found = useRunningOneShot(going === "running" && !announced ? a : undefined);
	const run = runOf(read, announced ?? found);
	const loopId = run?.loopId ?? a.id;
	const open = oneShots?.onOpen;
	const brief = a.follow_up ?? a.goal ?? "";
	return (
		<div className="one-shot-card">
			<Collapsible
				tone={`tool one-shot ${going === "running" ? "running" : status === "passed" ? "done" : "error"}`}
				label={
					<>
						<span className="tool-name">one-shot{a.follow_up ? " · 跟进" : ""}</span>
						{read && <span>第 {read.run}/{read.of} 次运行</span>}
						<span className="tool-summary">{a.change ? `${a.change} · ` : ""}{brief.split("\n")[0]}</span>
						<span className="tool-state">{ONE_SHOT_STATUS[status]}</span>
						{loopId && open && (
							<button
								className="link"
								onClick={(e) => {
									e.stopPropagation();
									open(loopId);
								}}
							>
								在 Loops 中查看
							</button>
						)}
					</>
				}
			>
				<pre className="code-block">
					<code>{brief}</code>
				</pre>
				{result && (
					<pre className="code-block">
						<code>{read?.body || textOf(result.content) || "（无输出）"}</code>
					</pre>
				)}
			</Collapsible>
			{run && (
				<div className="one-shot-run">
					<RunTrace
						loopId={run.loopId}
						iterationId={run.iterationId}
						running={going === "running"}
						onEnded={setEnded}
						endedNote={check ? <CheckLine check={check} /> : "验收中…"}
					/>
				</div>
			)}
		</div>
	);
}

/** The check the platform is running on a one-shot's run, with a clock from when the card heard it start. */
function CheckLine({ check }: { check: OneShotCheck }) {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, []);
	const secs = Math.max(0, Math.floor((now - check.at) / 1000));
	return (
		<>
			验收 {check.index + 1}/{check.of} · <code>{check.run}</code> · {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}
		</>
	);
}

function ToolCall({
	sessionId,
	callId,
	name,
	args,
	result,
	components,
	delegates,
	oneShots,
	live = false,
}: {
	sessionId: string;
	callId: string;
	name: string;
	args: unknown;
	result?: ToolResultMessage;
	components: ProfileSummary["components"];
	delegates?: DelegateProps;
	oneShots?: OneShotProps;
	/** Whether the run this call is in is still going. */
	live?: boolean;
}) {
	const summary = useMemo(() => summarizeArgs(args), [args]);

	const state = !result ? "running" : result.isError ? "error" : "done";

	if (name === "delegate") return <DelegateCard callId={callId} args={args} result={result} delegates={delegates} />;
	if (name === "one_shot") return <OneShotCard callId={callId} args={args} result={result} live={live} oneShots={oneShots} />;

	// A hit swaps in the registered renderer, and that is all the reader gets: the component is the
	// output, and the arguments behind it are in the session's own transcript for anyone debugging.
	// A miss (the common case: every builtin/MCP/extension tool call, and any profile that declares
	// no components at all) falls through to exactly the panel this always rendered — that fallback
	// must never change shape because of this lookup.
	const Renderer = lookupRenderer(name, components);
	if (Renderer) {
		return (
			<div className="tool-component">
				<Renderer sessionId={sessionId} callId={callId} args={(args as Record<string, unknown>) ?? {}} result={result} />
			</div>
		);
	}

	return (
		<Collapsible
			tone={`tool ${state}`}
			label={
				<>
					<span className="tool-name">{name}</span>
					<span className="tool-summary">{summary}</span>
					<span className="tool-state">{state === "running" ? "运行中…" : state === "error" ? "失败" : ""}</span>
				</>
			}
		>
			<pre className="code-block">
				<code>{JSON.stringify(args ?? {}, null, 2)}</code>
			</pre>
			{result && <ToolOutput name={name} content={result.content} />}
		</Collapsible>
	);
}

/** A tool result's text, and beside it any images it returned, such as a screenshot. */
function ToolOutput({ name, content }: { name: string; content: ToolResultMessage["content"] }) {
	const text = textOf(content);
	const images = imageSources(content);
	return (
		<>
			{(text || images.length === 0) && (
				<pre className="code-block">
					<code>{text || "（无输出）"}</code>
				</pre>
			)}
			{images.map((src, i) => (
				<img key={i} className="tool-image" src={src} alt={`${name} #${i + 1}`} />
			))}
		</>
	);
}

function AssistantContent({
	message,
	results,
	sessionId,
	components,
	delegates,
	oneShots,
	live = false,
}: {
	message: AssistantMessage;
	results: Map<string, ToolResultMessage>;
	sessionId: string;
	components: ProfileSummary["components"];
	delegates?: DelegateProps;
	oneShots?: OneShotProps;
	/** Whether the run this message is in is still going. */
	live?: boolean;
}) {
	return (
		<>
			{message.content.map((block, i) => {
				if (block.type === "text") return <Markdown key={i} text={block.text} sessionId={sessionId} />;
				// No `thinking` case: groupIntoRuns lifts every thinking block out of the message and
				// into its segment's ThinkingBox, so none of them reach this far.
				if (block.type === "toolCall")
					return (
						// Keyed by call id, never by array index: blocks stream in, so an index can
						// shift under a tool call that is already on screen. React then treats it as
						// a different element and remounts it, throwing away whatever the user had
						// typed into an interactive component mid-edit.
						<ToolCall
							key={block.id}
							sessionId={sessionId}
							callId={block.id}
							name={block.name}
							delegates={delegates}
							oneShots={oneShots}
							live={live}
							args={block.arguments}
							result={results.get(block.id)}
							components={components}
						/>
					);
				return null;
			})}
			{message.errorMessage && <p className="error">{message.errorMessage}</p>}
		</>
	);
}

function AssistantView(props: {
	message: AssistantMessage;
	results: Map<string, ToolResultMessage>;
	sessionId: string;
	components: ProfileSummary["components"];
}) {
	return (
		<div className="msg assistant">
			<AssistantContent {...props} />
		</div>
	);
}

/**
 * Seconds spent thinking in this stretch, counted only for a box this tab actually watched.
 * Counts while `live` and resumes from where it stopped when `live` comes back, so a segment that
 * thinks, calls a tool and thinks again shows the sum rather than the last stretch alone.
 *
 * A box restored from history shows no duration rather than a number that is quietly wrong:
 * message timestamps are stamped when a message starts, so they cannot say when one finished.
 */
function useElapsed(live: boolean): number {
	const [seconds, setSeconds] = useState(0);
	const accumulatedMs = useRef(0);
	useEffect(() => {
		if (!live) return;
		const base = accumulatedMs.current;
		const started = Date.now();
		const tick = () => {
			accumulatedMs.current = base + (Date.now() - started);
			setSeconds(Math.round(accumulatedMs.current / 1000));
		};
		const timer = setInterval(tick, 1000);
		return () => {
			clearInterval(timer);
			tick();
		};
	}, [live]);
	return seconds;
}

/**
 * The shell both run boxes share.
 *
 * A box steers itself only until the reader touches it: while the run is live and nobody has
 * clicked, it may open on its own and it follows its own tail under a height cap, so a long run
 * cannot take over the screen. The first click hands it over for good -- and takes the cap off
 * with it, because a cap that cannot be lifted is just a box you are not allowed to read.
 */
function RunBox({
	tone,
	live,
	autoOpen,
	label,
	follow,
	children,
}: {
	tone: string;
	live: boolean;
	/** Whether a live run opens this box unasked. The thinking does; the tool list stays shut. */
	autoOpen: boolean;
	label: React.ReactNode;
	/** Changes whenever there is more to see, so the box knows to scroll to its own tail. */
	follow: unknown;
	children: React.ReactNode;
}) {
	const [toggled, setToggled] = useState<boolean | null>(null);
	const body = useRef<HTMLDivElement>(null);
	const following = live && toggled === null;
	const open = toggled ?? (live && autoOpen);

	useEffect(() => {
		if (!following) return;
		const el = body.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [follow, following]);

	return (
		<div className={`collapsible ${tone}${following ? " following" : ""}`}>
			{/* While the box is still steering itself, the first click means "this one is mine" -- it
			    takes the box over and uncaps it rather than shutting it, because the reader clicking a
			    box that is too small to read wants more of it, not less. After that it just toggles. */}
			<button className="collapsible-head" onClick={() => setToggled(following ? true : !open)} aria-expanded={open}>
				<span className="caret">{open ? "▾" : "▸"}</span>
				{label}
			</button>
			{open && (
				<div className="collapsible-body" ref={body}>
					{children}
				</div>
			)}
		</div>
	);
}

/** Everything the run thought, in one box, so what stays on screen is the answer instead. */
function ThinkingBox({ thinking, live }: { thinking: string; live: boolean }) {
	const seconds = useElapsed(live);
	const label = live ? `思考中… ${seconds}s` : seconds > 0 ? `已深度思考（用时 ${seconds} 秒）` : "已深度思考";
	return (
		<RunBox tone="thinking" live={live} autoOpen follow={thinking} label={<span className="tool-name">{label}</span>}>
			<Markdown text={thinking} />
		</RunBox>
	);
}

/**
 * Everything the run did, in one box. Shut even while the run is live: the thinking box is already
 * open and moving, and two boxes growing at once is churn rather than progress -- the header says
 * what is running, which is the part worth watching.
 */
function ToolsBox({
	calls,
	results,
	sessionId,
	components,
	live,
	delegates,
	oneShots,
}: {
	calls: ToolCallContent[];
	results: Map<string, ToolResultMessage>;
	sessionId: string;
	components: ProfileSummary["components"];
	live: boolean;
	delegates?: DelegateProps;
	oneShots?: OneShotProps;
}) {
	const failed = calls.filter((c) => results.get(c.id)?.isError).length;
	const running = live ? calls.find((c) => !results.has(c.id)) : undefined;

	const label = running ? (
		<>
			<span className="tool-running">⟳</span>
			<span className="tool-name">{running.name}</span>
			<span className="tool-summary">{summarizeArgs(running.arguments)}</span>
			<span className="tool-state">第 {calls.length} 步</span>
		</>
	) : (
		<>
			<span className="tool-name">已调用 {calls.length} 个工具</span>
			{failed > 0 && <span className="tool-state">{failed} 个失败</span>}
		</>
	);

	return (
		<RunBox tone="tools" live={live} autoOpen={false} follow={calls.length} label={label}>
			{calls.map((call) => (
				<ToolCall
					key={call.id}
					sessionId={sessionId}
					callId={call.id}
					name={call.name}
					args={call.arguments}
					result={results.get(call.id)}
					components={components}
					delegates={delegates}
					oneShots={oneShots}
					live={live}
				/>
			))}
		</RunBox>
	);
}

/** A message in a run that is not the agent's reply: a package's own status line, drawn as the one sentence it carries. */
function Notice({ message }: { message: Message }) {
	const notice = noticeFor(message);
	return notice.kind === "note" ? <div className="msg notice">{notice.text}</div> : null;
}

/**
 * One run: for each stretch of it, what it thought, what it did, and what
 * it then said. Only the last stretch can still be running, so only its boxes follow along.
 */
function RunView({
	action,
	run,
	live,
	results,
	sessionId,
	components,
	delegates,
	oneShots,
}: {
	action?: React.ReactNode;
	run: Run;
	/** True for the one run still streaming — the box that follows along and then collapses itself. */
	live: boolean;
	results: Map<string, ToolResultMessage>;
	sessionId: string;
	components: ProfileSummary["components"];
	delegates?: DelegateProps;
	oneShots?: OneShotProps;
}) {
	return (
		<div className="msg assistant agent-run">
			{run.segments.map((segment, s) => {
				// Keyed by position, which is stable for every segment but the last — and the last
				// is the only one that changes, since a run only ever grows at its end.
				const running = live && s === run.segments.length - 1;
				return (
					<Fragment key={s}>
						{(segment.thinking !== "" || segment.toolCalls.length > 0) && (
							<div className="run-chips">
								{segment.thinking !== "" && <ThinkingBox thinking={segment.thinking} live={running && run.thinkingOpen} />}
								{segment.toolCalls.length > 0 && (
									<ToolsBox calls={segment.toolCalls} results={results} sessionId={sessionId} components={components} live={running} delegates={delegates} oneShots={oneShots} />
								)}
							</div>
						)}
						{segment.messages.map((m, i) =>
							m.role === "assistant" ? (
								<AssistantContent
									key={i}
									message={m as AssistantMessage}
									results={results}
									sessionId={sessionId}
									components={components}
									delegates={delegates}
									oneShots={oneShots}
									live={running}
								/>
							) : (
								<Notice key={i} message={m} />
							),
						)}
					</Fragment>
				);
			})}
			{action}
		</div>
	);
}

/**
 * Longer than this and a user bubble is shown collapsed.
 *
 * A slash command's message is the ENTIRE expanded workflow -- pi substitutes the template
 * before it builds the user message -- so `/apply` is an 89 KB bubble that buries the
 * conversation. The same treatment catches a long paste, which reads identically from here:
 * there is no signal in the message saying a command produced it.
 */
const LONG_USER_MESSAGE = 1200;

/** Branching from a row, and the branches already cut there (ADR-0016). */
export interface BranchProps {
	/** Every session in the list; the ones cut from this session are drawn as markers. */
	sessions: SessionSummary[];
	onBranch: (entryId: string) => void;
	onOpen: (id: string) => void;
}

/** One line per branch cut inside the row above, linking to it. */
function ForkMarkers({ forks, onOpen }: { forks: SessionSummary[]; onOpen: (id: string) => void }) {
	return (
		<>
			{forks.map((s) => (
				<p key={s.id} className="fork-marker">
					↳ 由此分支：
					<button className="link" onClick={() => onOpen(s.id)}>
						{s.title ?? "新对话"}
					</button>
				</p>
			))}
		</>
	);
}

function MessageMenu({ entryId, onBranch, open, onOpen, onClose }: {
	entryId: string;
	onBranch: (entryId: string) => void;
	open: boolean;
	onOpen: () => void;
	onClose: () => void;
}) {
	const menuId = useId();
	const container = useRef<HTMLDivElement>(null);
	const trigger = useRef<HTMLButtonElement>(null);
	const command = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (!open) return;
		command.current?.focus();
		const outside = (event: PointerEvent) => {
			if (event.target instanceof Node && !container.current?.contains(event.target)) onClose();
		};
		const escape = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			onClose();
			trigger.current?.focus();
		};
		document.addEventListener("pointerdown", outside);
		document.addEventListener("keydown", escape);
		return () => {
			document.removeEventListener("pointerdown", outside);
			document.removeEventListener("keydown", escape);
		};
	}, [open, onClose]);

	return (
		<div ref={container} className="message-menu" onBlur={(event) => {
			if (!event.currentTarget.contains(event.relatedTarget)) onClose();
		}}>
			<button
				ref={trigger}
				type="button"
				className="ghost message-more"
				aria-label="更多"
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={open ? menuId : undefined}
				onClick={() => open ? onClose() : onOpen()}
				onKeyDown={(event) => {
					if (event.key === "ArrowDown") {
						event.preventDefault();
						onOpen();
					}
				}}
			>
				<svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
					<circle cx="5" cy="12" r="1.8" />
					<circle cx="12" cy="12" r="1.8" />
					<circle cx="19" cy="12" r="1.8" />
				</svg>
			</button>
			{open && (
				<div id={menuId} className="message-popover" role="menu" aria-label="更多">
					<button ref={command} type="button" className="ghost" role="menuitem" onClick={() => {
						onClose();
						trigger.current?.focus();
						onBranch(entryId);
					}}>
						由此分支
					</button>
				</div>
			)}
		</div>
	);
}

export function Messages({
	messages,
	sessionId,
	components,
	streaming,
	speakers,
	branches,
	delegates,
	oneShots,
}: {
	messages: Message[];
	sessionId: string;
	/** The current profile's declared components. Empty when it declares none, or while the profile list is still loading. */
	components: ProfileSummary["components"];
	/** Whether the agent is mid-run. Only the last run can be the one it is in. */
	streaming: boolean;
	/** In a shared session: who wrote first (the owner) and who is reading, both by name. */
	speakers?: { first: string; me: string; nameOf: (userId: string) => string; senders?: Record<string, string> };
	/** Absent where nobody can branch, such as a loop iteration's transcript. */
	branches?: BranchProps;
	/** Opening a `delegate` call's child session. Absent where nothing can be opened. */
	delegates?: DelegateProps;
	/** The runs of this conversation's `one_shot` calls announced while they run. */
	oneShots?: OneShotProps;
}) {
	const results = useMemo(() => {
		const map = new Map<string, ToolResultMessage>();
		for (const m of messages) if (m.role === "toolResult") map.set((m as ToolResultMessage).toolCallId, m as ToolResultMessage);
		return map;
	}, [messages]);

	// A one-shot is watched while it runs, so its card stays in the stream rather than in the folded tools box.
	const rows = useMemo(() => groupIntoRuns(messages, (name) => name === "one_shot" || !!lookupRenderer(name, components)), [messages, components]);

	const [openMenu, setOpenMenu] = useState<{ sessionId: string; entryId: string } | null>(null);
	const closeMenu = useCallback(() => setOpenMenu(null), []);
	const points = useMemo(() => rows.map((row, i) =>
		branches && !(streaming && i === rows.length - 1) ? branchPointOf(messages, row, rows[i + 1]) : null,
	), [branches, streaming, messages, rows]);
	useEffect(() => {
		if (openMenu && (openMenu.sessionId !== sessionId || !points.includes(openMenu.entryId))) setOpenMenu(null);
	}, [openMenu, sessionId, points]);

	// A shared session labels each message with its speaker: the sender the server recorded, and
	// the handover lines for messages written before it recorded any.
	const first = speakers?.first;
	const senders = speakers?.senders;
	const nameOf = speakers?.nameOf;
	const speakerOf = useMemo(() => {
		const byRow = new Map<number, string>();
		if (!first) return byRow;
		const userRows = rows.filter((r): r is UserRow => r.kind === "user");
		const fromLines = speakerNames(
			userRows.map((r) => textOf(r.message.content)),
			first,
		);
		let carried = first;
		userRows.forEach((row, i) => {
			const recorded = senders?.[(row.message as { entryId?: string }).entryId ?? ""];
			carried = recorded && nameOf ? nameOf(recorded) : fromLines[i]!;
			byRow.set(row.index, carried);
		});
		return byRow;
	}, [rows, first, senders, nameOf]);

	return (
		<div className="message-list">
			{rows.map((row, i) => {
				// Only the last row can still be running, and only while the session is streaming.
				const live = streaming && i === rows.length - 1;
				const next = rows[i + 1];
				const point = points[i];
				const action = point && branches ? (
					<MessageMenu
						key={point}
						entryId={point}
						onBranch={branches.onBranch}
						open={openMenu?.sessionId === sessionId && openMenu.entryId === point}
						onOpen={() => setOpenMenu({ sessionId, entryId: point })}
						onClose={closeMenu}
					/>
				) : null;
				const forks = branches ? forkedAt(messages, row, next, branches.sessions, sessionId) : [];
				if (row.kind === "run")
					return (
						<Fragment key={row.index}>
							<RunView action={action} run={row} live={live} results={results} sessionId={sessionId} components={components} delegates={delegates} oneShots={oneShots} />
							{branches && <ForkMarkers forks={forks} onOpen={branches.onOpen} />}
						</Fragment>
					);

				const raw = textOf(row.message.content);
				const handover = parseHandover(raw);
				// The handover line comes first, then the video panel's watching line, then what was typed.
				const said = handover ? handover.rest : raw;
				const watching = parseWatching(said);
				const text = watching ? watching.rest : said;
				const name = speakerOf.get(row.index);
				const author = !name || name === speakers?.me ? "You" : name;
				// A divider wherever the speaker changes, whether a line said so or the record did.
				const previousUser = rows.slice(0, i).reverse().find((r): r is UserRow => r.kind === "user");
				const changedSpeaker = Boolean(name && previousUser && speakerOf.get(previousUser.index) !== name) || Boolean(handover);
				const firstLine = text.split("\n", 1)[0]!.slice(0, 80);
				return (
					<Fragment key={row.index}>
						{changedSpeaker && <p className="handover">— {name} 接手 —</p>}
						<div className="msg user">
							{speakers && author !== "You" && <div className="message-author">{author}</div>}
							<div className="user-content">
								{text.length > LONG_USER_MESSAGE ? (
									<Collapsible
										tone="long-user"
										label={
											<>
												<span className="tool-summary">{firstLine}</span>
												<span className="tool-state">{Math.round(text.length / 1000)}k 字符</span>
											</>
										}
									>
										<div className="bubble">{text}</div>
									</Collapsible>
								) : (
									<div className="bubble">{text}</div>
								)}
							</div>
							{watching && <WatchingChip label={formatClock(watching.seconds)} seconds={watching.seconds} />}
							{action}
						</div>
						{branches && <ForkMarkers forks={forks} onOpen={branches.onOpen} />}
					</Fragment>
				);
			})}
		</div>
	);
}
